import { Fragment, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { RadioGroup } from '@anyui/core';
import { cn } from '@anytag/shared-utils';
import { BaseModal, Radio } from '@anytag/shared-atoms';
import { useParamsSearch, useQueryHelper } from '@anytag/shared-hooks';
import { AnalyticsFormValues, AnalyticsConnectableAccountsSearchStateSchemaType } from '../schemaTypes';
import { ConnectableAccount, ConnectableAccountType } from './ConnectableAccount';

interface ConnectableAccountsModalState {
  title: string;
  open: boolean;
  description: string;
  data: { accounts?: ConnectableAccountType[]; mainAccount?: Omit<ConnectableAccountType, 'isUsed' | 'isSelected'> }[];
}

export const ConnectableAccountsModal = () => {
  const { watch, setValue } = useFormContext<AnalyticsFormValues>();
  const { t, navigate, enqueueSnackbar } = useQueryHelper();
  const { state } = useParamsSearch<AnalyticsConnectableAccountsSearchStateSchemaType>();

  const connectableAccounts = state?.connectableAccounts || {};
  const values = watch();

  useEffect(() => {
    // We set twitter account directly because it has 1 connectable account only
    if (connectableAccounts.twitter) {
      setValue('twitter', {
        name: connectableAccounts.twitter.name,
        twitterUserId: connectableAccounts.twitter.twitterUserId
      });
      enqueueSnackbar(t('succeededInConnecting', { name: connectableAccounts.twitter.name }), { variant: 'success' });
    }
  }, [connectableAccounts.twitter?.twitterUserId]);

  const { open, title, data, description } = (() => {
    let modalState: ConnectableAccountsModalState = { data: [], title: '', open: false, description: '' };

    if (connectableAccounts.instagram?.length) {
      modalState = {
        open: true,
        title: t('Choose Instagram Account'),
        description: t('Select an Instagram account to connect to'),
        data: connectableAccounts.instagram.map((account) => ({
          mainAccount: {
            id: account.page.id,
            name: account.page.name,
            socialMedia: 'FACEBOOK_PAGE',
            avatar: account.page.avatar || '',
            followers: account.page.followersCount
          },
          accounts: [
            {
              socialMedia: 'INSTAGRAM',
              id: account.igAccount.id,
              name: account.igAccount.name,
              isUsed: account.isAlreadyUsed,
              avatar: account.igAccount.avatar || '',
              followers: account.igAccount.followersCount,
              isSelected: values.instagram?.id === +account.igAccount.id
            }
          ]
        }))
      };
    } else if (connectableAccounts.youtube?.length) {
      modalState = {
        open: true,
        title: t('Choose Youtube Channel'),
        description: t('Select an YouTube channel to connect to'),
        data: [
          {
            accounts: connectableAccounts.youtube.map((account) => ({
              socialMedia: 'YOUTUBE',
              id: account.channelId,
              avatar: account.avatar,
              isUsed: account.isUsed,
              name: account.channelName,
              followers: account.subscribersCount,
              isSelected: values.youtube?.channelId === account.channelId
            }))
          }
        ]
      };
    } else if (connectableAccounts.facebookPage?.pages.length) {
      modalState = {
        open: true,
        title: t('Choose Facebook Page'),
        description: t('Select the Facebook Page to connect to'),
        data: [
          {
            mainAccount: connectableAccounts.facebookPage.account
              ? {
                  socialMedia: 'FACEBOOK_PAGE',
                  id: connectableAccounts.facebookPage.account.id,
                  name: connectableAccounts.facebookPage.account.name,
                  avatar: connectableAccounts.facebookPage.account.avatar,
                  friends: connectableAccounts.facebookPage.account.friendCount
                }
              : undefined,
            accounts: connectableAccounts.facebookPage.pages.map((account) => ({
              id: account.id,
              name: account.name,
              avatar: account.avatar,
              socialMedia: 'FACEBOOK_PAGE',
              followers: account.followers,
              isUsed: account.isAlreadyUsed,
              isSelected: values.facebookPage?.fbPageId === account.id
            }))
          }
        ]
      };
    }

    return modalState;
  })();

  const closeConnectableAccountsModal = () => {
    navigate({});
  };

  const handleSelectAccount = (account: ConnectableAccountType, mainAccount?: ConnectableAccountType) => {
    switch (account.socialMedia) {
      case 'YOUTUBE':
        setValue('youtube', values.youtube ? undefined : { channelId: account.id, channelName: account.name });
        break;
      case 'INSTAGRAM':
        setValue(
          'instagram',
          values.instagram
            ? undefined
            : {
                id: +account.id,
                businessAccountId: '',
                fbPageName: account.name,
                fbPageId: mainAccount?.id || '',
                instagramAccountName: mainAccount?.name || ''
              }
        );
        break;
      case 'FACEBOOK_PAGE':
        setValue('facebookPage', values.facebookPage ? undefined : { fbPageId: account.id, fbPageName: account.name });
        break;
      default:
        break;
    }
  };

  return (
    <BaseModal
      size="content"
      open={open}
      title={title}
      description={description}
      onClose={closeConnectableAccountsModal}
      footer={{
        cancelButton: { onClick: closeConnectableAccountsModal },
        submitButton: { title: t('Button.Add'), onClick: closeConnectableAccountsModal }
      }}
    >
      <div className="min-w-112.5">
        {data.map(({ mainAccount, accounts }, index) => (
          <Fragment key={index}>
            {mainAccount ? (
              <ConnectableAccount className="mt-2" account={mainAccount} socialMedia={mainAccount.socialMedia} />
            ) : null}
            <RadioGroup direction="vertical" value={accounts?.find((account) => account.isSelected)?.id || ''}>
              {accounts?.map((account, idx) => (
                <div key={`account-${account.id}-${idx}`} className={'relative mt-2 w-full'}>
                  <Radio
                    value={account.id}
                    disabled={account.isUsed}
                    onClick={() => handleSelectAccount(account, mainAccount)}
                    label={
                      <ConnectableAccount account={account} className="p-2.5 pl-0" socialMedia={account.socialMedia} />
                    }
                    className={cn(
                      'h-16.5 grid-cols-[max-content_auto]! rounded-md border-2',
                      "[&_[role='radio']]:box-border [&_[role='radio']]:h-full [&_[role='radio']]:w-max [&_[role='radio']]:items-center [&_[role='radio']]:p-2.5 [&_[role='radio']]:pr-0",
                      account.isSelected ? 'border-blue-70' : 'border-black-30'
                    )}
                  />

                  {account.isUsed && (
                    <div className="absolute top-0 left-0 z-1 flex h-17.5 w-full cursor-default items-center justify-center rounded-md bg-black-90/60 text-base leading-none font-semibold tracking-normal text-white">
                      {t('This account already exists')}
                    </div>
                  )}
                </div>
              ))}
            </RadioGroup>
          </Fragment>
        ))}
      </div>
    </BaseModal>
  );
};
